// @flow
import { forEach, cloneDeep } from 'lodash';
import { DEFAULT_FILTERS_ID, DEFAULT_FETCH_COUNT } from './actions';
import type { State, Action } from './types';
import type { CurrentFilters } from '../onDemand/types';

export const DEFAULT_FILTERS: CurrentFilters = {
  id: DEFAULT_FILTERS_ID,
  genres: [],
  itemStart: 1,
  ratings: [],
  sort: '',
  totalItems: DEFAULT_FETCH_COUNT,
  unlocked_content_only: false,
  video_type: true, // true for full episodes only,
};

const initialState: State = {
  content: {
    fetching: false,
    error: false,
    items: {},
    counts: {},
  },
  network: {
    fetching: false,
    error: false,
    details: null,
  },
  categories: {
    fetching: false,
    error: false,
    items: [],
  },
  currentFilters: cloneDeep(DEFAULT_FILTERS),
  networkSlug: undefined,
};

const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case 'REQUEST_NETWORK_DATA': {
      const { networkSlug } = action;
      return {
        ...state,
        network: {
          ...state.network,
          fetching: true,
          error: false,
        },
        networkSlug,
        content: {
          fetching: false,
          error: false,
          items: {},
          counts: {},
        },
      };
    }
    case 'RECEIVE_NETWORK_DATA': {
      const { payload, networkSlug } = action;
      return {
        ...state,
        network: {
          ...state.network,
          fetching: false,
          error: false,
          details: payload,
        },
        networkSlug,
      };
    }
    case 'RECEIVE_NETWORK_DATA_ERROR':
      return {
        ...state,
        network: {
          ...state.network,
          fetching: false,
          details: null,
          error: true,
        },
      };
    case 'REQUEST_NETWORK_CONTENT': {
      return {
        ...state,
        content: {
          ...state.content,
          fetching: true,
          error: false,
          items: {
            ...state.content.items,
          },
          counts: {
            ...state.content.counts,
          },
        },
      };
    }
    case 'RECEIVE_NETWORK_CONTENT': {
      const { concatResults, contentType, payload, filtersId } = action;
      if (state.currentFilters.id !== filtersId) {
        console.warn('received network content for outdated filters, ignore it');
        return { ...state };
      }
      const items = [];

      if (concatResults) {
        items.push(...(state.content.items[contentType] || []));
      }

      forEach(['shows', 'movies', 'extras'], key => {
        forEach(payload[key], entry => {
          forEach(entry, item => {
            items.push(item);
          });
        });
      });

      return {
        ...state,
        content: {
          ...state.content,
          fetching: false,
          error: false,
          items: {
            ...state.content.items,
            [contentType]: items,
          },
          counts: {
            ...state.content.counts,
            [contentType]: payload.count,
          },
        },
      };
    }

    case 'REQUEST_NETWORK_CONTENT_ERROR': {
      const { filtersId } = action;
      if (state.currentFilters.id !== filtersId) {
        console.warn('got error on network content request for outdated filters, ignore it');
        return { ...state };
      }
      return {
        ...state,
        content: {
          ...state.content,
          fetching: false,
          error: true,
          items: {},
        },
      };
    }
    case 'REQUEST_NETWORK_CATEGORIES':
      return {
        ...state,
        categories: {
          ...state.categories,
          fetching: true,
          error: false,
          items: [],
        },
      };
    case 'RECEIVE_NETWORK_CATEGORIES':
      return {
        ...state,
        categories: {
          ...state.categories,
          fetching: false,
          error: false,
          items: action.payload,
        },
        currentFilters: cloneDeep(DEFAULT_FILTERS),
      };
    case 'RECEIVE_NETWORK_CATEGORIES_ERROR':
      return {
        ...state,
        categories: {
          ...state.categories,
          fetching: false,
          error: true,
          items: [],
        },
        currentFilters: cloneDeep(DEFAULT_FILTERS),
      };
    case 'SAVE_NETWORK_FILTERS':
      return {
        ...state,
        currentFilters: action.payload,
      };
    default:
      return state;
  }
};

export default reducer;
